import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';

const Nav = styled.nav`
  background: ${props => props.scrolled ? '#0E1114' : 'rgba(14, 17, 20, 0.95)'};
  backdrop-filter: blur(10px);
  padding: ${props => props.scrolled ? '1rem 2rem' : '1.5rem 2rem'};
  box-shadow: ${props => props.scrolled
    ? '0 4px 20px rgba(0, 0, 0, 0.5)'
    : '0 2px 10px rgba(0, 0, 0, 0.3)'};
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: 2px solid ${props => props.scrolled ? '#F5B800' : 'rgba(245, 184, 0, 0.5)'};
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  color: white;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  span:first-child {
    font-size: 2.2rem;
    letter-spacing: 3px;
    color: #F5B800;
    text-shadow: 0 0 20px rgba(245, 184, 0, 0.5);
    font-weight: 900;
  }

  span:last-child {
    font-size: 0.7rem;
    font-weight: 600;
    opacity: 0.9;
    letter-spacing: 2px;
    margin-top: 0.5rem;
    color: #2EC4B6;
    text-transform: uppercase;
  }
`;

const NavLinksWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: row-reverse;
    gap: 0.5rem;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #0E1114;
    padding: 2rem;
    gap: 0.5rem;
    border-top: 2px solid #F5B800;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.8);
  }
`;

const NavLink = styled(Link)`
  color: white;
  font-weight: 600;
  font-size: 1.05rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  position: relative;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: 0.5px;

  ${props => props.$active && `
    background: #2C2F33;
    color: #F5B800;
    box-shadow: inset 0 0 0 2px #F5B800;
  `}

  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #F5B800;
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover:before {
    transform: ${props => props.$active ? 'scaleX(0)' : 'scaleX(1)'};
  }

  &:hover {
    color: #F5B800;
    background: ${props => props.$active ? '#2C2F33' : 'rgba(44, 47, 51, 0.5)'};
  }
`;

const MenuButton = styled.button`
  display: none;
  background: #F5B800;
  border: none;
  color: #0E1114;
  font-size: 1.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 4px;
  box-shadow: 0 4px 15px rgba(245, 184, 0, 0.4);
  transition: all 0.3s ease;
  font-weight: 900;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(245, 184, 0, 0.6);
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const DarkModeToggle = styled.button`
  background: ${props => props.$darkMode ? '#F5B800' : '#2C2F33'};
  border: 2px solid #F5B800;
  color: ${props => props.$darkMode ? '#0E1114' : '#F5B800'};
  font-size: 1.3rem;
  padding: 0.6rem 1rem;
  border-radius: 4px;
  box-shadow: 0 4px 15px rgba(245, 184, 0, 0.3);
  transition: all 0.3s ease;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(245, 184, 0, 0.5);
  }

  @media (max-width: 768px) {
    margin-right: 0.5rem;
  }
`;

const NavControls = styled.div`
  display: flex;
  align-items: center;
`;

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <Nav scrolled={scrolled}>
      <NavContainer>
        <Logo to="/">
          <span>VITAL-LINK</span>
          <span>Structured Wiring & Advanced Engineering</span>
        </Logo>
        <NavLinksWrapper>
          <NavLinks isOpen={isOpen}>
            {navItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                $active={location.pathname === item.path}
              >
                {item.label}
              </NavLink>
            ))}
          </NavLinks>
          <NavControls>
            <DarkModeToggle onClick={toggleDarkMode} $darkMode={darkMode}>
              {darkMode ? '☀️' : '🌙'}
            </DarkModeToggle>
            <MenuButton onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? '✕' : '☰'}
            </MenuButton>
          </NavControls>
        </NavLinksWrapper>
      </NavContainer>
    </Nav>
  );
};

export default Navigation;
